import { useState, useCallback, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Upload, X, AlertCircle, FolderOpen } from 'lucide-react'
import { useUploadDocuments } from '@/hooks/useDocuments'
import { useDropzone } from 'react-dropzone'

const MAX_FILES = 200

const ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md', '.xlsx', '.csv', '.tsv', '.jpg', '.jpeg', '.png', '.gif', '.tif', '.tiff']

interface UploadDocumentsModalProps {
  kbId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function UploadDocumentsModal({ kbId, open, onOpenChange, onSuccess }: UploadDocumentsModalProps) {
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const uploadMutation = useUploadDocuments(kbId)
  const folderInputRef = useRef<HTMLInputElement>(null)

  const filterValidFiles = (fileList: File[]) => {
    return fileList.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase()
      return ACCEPTED_EXTENSIONS.includes(ext)
    })
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null)
    const totalFiles = files.length + acceptedFiles.length
    if (totalFiles > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed per upload. You selected ${totalFiles} files.`)
      return
    }
    setFiles(prev => [...prev, ...acceptedFiles])
  }, [files.length])

  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList) return
    
    const validFiles = filterValidFiles(Array.from(fileList))
    const totalFiles = files.length + validFiles.length
    
    if (totalFiles > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed per upload. Found ${validFiles.length} valid files in folder.`)
      return
    }
    
    if (validFiles.length === 0) {
      setError('No supported files found in the selected folder.')
      return
    }
    
    setError(null)
    setFiles(prev => [...prev, ...validFiles])
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      // Documents
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      // Tables
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'text/tab-separated-values': ['.tsv'],
      // Images
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/tiff': ['.tif', '.tiff'],
    }
  })

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (files.length === 0) return
    
    try {
      await uploadMutation.mutateAsync(files)
      setFiles([])
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Hidden folder input */}
          <input
            ref={folderInputRef}
            type="file"
            className="hidden"
            onChange={handleFolderSelect}
            {...{ webkitdirectory: '', directory: '' } as any}
            multiple
          />

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            {isDragActive ? (
              <p className="text-sm">Drop the files here...</p>
            ) : (
              <div>
                <p className="text-sm font-medium mb-1">
                  Drag & drop files here, or click to select
                </p>
                <p className="text-xs text-muted-foreground">
                  Documents: PDF, DOC, DOCX, TXT, MD<br />
                  Tables: XLSX, CSV, TSV<br />
                  Images: JPEG, JPG, PNG, TIF, GIF<br />
                  <span className="text-muted-foreground/70">Maximum {MAX_FILES} files per upload</span>
                </p>
              </div>
            )}
          </div>

          {/* Folder selection button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation()
              folderInputRef.current?.click()
            }}
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            Select Folder
          </Button>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Selected Files ({files.length})</h4>
              <div className="max-h-[200px] overflow-y-auto space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-muted rounded-md"
                  >
                    <span className="text-sm truncate flex-1">{file.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={files.length === 0 || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? 'Uploading...' : `Upload ${files.length} file(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
